import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  List,
  ListItem,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import {
  CAResponse,
  BackPlanResult,
  ContinuousAppointmentDto,
  AppointmentDto,
} from "../../api-client";
import FlatCard from "../../atoms/FlatCard";
import { AppointmentCard } from "../ScheduleOverview/Appointments/AppointmentSuggestion";
import notificationStore from "../../stores/Notification/notificationStore";

interface CAResponseDialogProps {
  open: boolean;
  handleClose: () => void;
  responseDialogData: CAResponse | null | undefined;
  saveAppointments: (appointments: AppointmentDto[]) => Promise<void>;
}

const CAResponseDialog: React.FC<CAResponseDialogProps> = ({
  open,
  handleClose,
  responseDialogData,
  saveAppointments,
}) => {

  // console.debug("CAResponseDialog rendered with data:", responseDialogData);

  // State for tracking selected appointments (default to all selected)
  const [selectedAppointments, setSelectedAppointments] = useState<{ [key: number]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize selectedAppointments when responseDialogData changes
  useEffect(() => {
    if (responseDialogData) {
      const newSelection: { [key: number]: boolean } = {};
      responseDialogData.backPlanResults?.forEach((_, index) => {
        newSelection[index] = true; // Default to all selected
      });
      setSelectedAppointments(newSelection);
    }
  }, [responseDialogData]);

  const caAppDto = responseDialogData?.savedCA;
  const backPlanResults = responseDialogData?.backPlanResults;

  const handleToggleAppointment = (index: number) => {
    setSelectedAppointments(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSaveSelectedAppointments = async () => {
    if (!backPlanResults) return;

    try {
      setIsSaving(true);
      
      // Filter selected appointments
      const selectedAppointmentsList = backPlanResults
        .filter((_, index) => selectedAppointments[index])
        .map(result => result.app);

      if (selectedAppointmentsList.length === 0) {
        notificationStore.showMessage("Bitte wählen Sie mindestens einen Termin aus", "warning");
        return;
      }

      await saveAppointments(selectedAppointmentsList);
      handleClose();
    } catch (error) {
      console.error("Error saving appointments:", error);
      notificationStore.showMessage("Fehler beim Speichern der Termine", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} style={{ zIndex: 2000 }} fullWidth maxWidth="lg">
      <DialogTitle id="alert-dialog-title">{"Zurückplanen"}</DialogTitle>
      <DialogContent>
        {!responseDialogData ? (
          // Loading state - show spinner while waiting for CAResponse data
          <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 200 }}>
            <Grid item>
              <CircularProgress size={60} />
              <Typography variant="h6" style={{ marginTop: 16, textAlign: 'center' }}>
                Termine werden erzeugt...
              </Typography>
            </Grid>
          </Grid>
        ) : (
          // Data available - show normal content
          caAppDto && (
            <>
              <FlatCard title="Dauertermin">
                <Grid container spacing={3}>
                  <Grid item xs={3} />
                  <Grid item xs={6}>
                    <AppointmentCard appointment={caAppDto} continous={true} />
                  </Grid>
                  <Grid item xs={3} />
                </Grid>
              </FlatCard>
              <FlatCard title="Ergebnisse der Zurückplanung">
                <List>
                  {backPlanResults?.map((result: BackPlanResult, index) => (
                    <ListItem key={index}>
                      <Grid container spacing={3} alignItems={"center"}>
                        <Grid item xs={2}>
                          <Typography>{new Date(result.date).toLocaleDateString()}</Typography>
                        </Grid>
                        <Grid item xs={5}>
                          {result.app && (
                            <AppointmentCard appointment={result.app} continous={false} />
                          )}
                        </Grid>
                        <Grid item xs={3}>
                          <Typography>{result.message}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={selectedAppointments[index] || false}
                                onChange={() => handleToggleAppointment(index)}
                                color="primary"
                              />
                            }
                            label={result.app.start ? `planen` : `als ungeplant speichern`}
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              </FlatCard>
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Abbrechen
        </Button>
        {responseDialogData && (
          <Button 
            onClick={handleSaveSelectedAppointments} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : "Planen"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CAResponseDialog;
